import {
  fetchAddCollectList,
  fetchAddLikeList,
  fetchPostAdEvent,
  fetchRemoveCollectList,
  fetchRemoveLikeList,
} from 'api'
import { AdvertisementType } from 'constant/advertisement'
import { FALSE, TRUE } from 'constant/common'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectAdInfo, selectEnableShare, selectTopicList } from 'redux/selector/app'
import { useImmer } from 'use-immer'
import PageWrapperInner from '../ViewShort/components/PageWrapper'
import { PlayerControlProvider, usePlayerControl } from '../ViewShort/components/PlayerPage'
import { VirtualPages } from '../ViewShort/components/VirtualPage'
import {
  FloatAdCloseButton,
  FloatAdCloseIcon,
  FloatAdImage,
  FloatAdWrapper,
  IconSearch,
  SearchButton,
  Wrapper,
} from './Styled'
import useAlertWorksActionResult from 'hooks/useAlertWorksActionResult'
import { useEvent } from 'hooks/useEvent'
import { reportShortVideoClick } from 'hooks/useAnalytics'
import { useMutex } from 'hooks/useMutex'
import { useSharedCache } from 'hooks/useDirtyWorkData'
import { flushSync } from 'react-dom'
import { PageWrapperNonScroll } from 'commonStyledComponents'
import { RESET_SHORT_FAVOURITE } from 'redux/constant/shortFavourite'
import { RESET_SHORT_COLLECTION } from 'redux/constant/shortCollection'
import { usePageActivated } from 'app-layered-layout/usePageActivated'
import { useNavigateTo } from 'hooks/useNavigateTo'
import { useShortcutBoxHandler } from 'hooks/useShortcutBoxHandler'
import useHintModal from 'hooks/useHintModal'
import { selectShareVideoData } from 'redux/selector/shareVideo'
import { shareVideoActions } from 'redux/slice/shareVideo'
import ShareVideoModal from 'component/ShareVideoModal'
import useOpenShareModal from 'page/SharePage/hooks/useOpenShareModal'
import {
  SchemaShortVideoShow,
  SchemaCmsIconuiShow,
  fetchShortWaterfallList,
  SchemaOholoMaterialShow,
} from 'api/generated'
import { formatDate } from 'utils/timeFormat'

// 5 video than ad like
// V V V V V A V V V V V A
const AD_INTERVAL = 5

/** N1-5268：瀑布流停留滿此秒數才算「看過一部」並上報 */
const WATCH_REPORT_DELAY = 5000

interface ImageCacheItem {
  src: string | null
}

interface ImageCache {
  [id: string]: ImageCacheItem
}

function Waterfall() {
  const dispatch = useDispatch()
  const [videoList, setVideoList] = useState<SchemaShortVideoShow[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const { navigateToSearchViaSpecific } = useNavigateTo()
  const openShareModal = useOpenShareModal()

  const fetchMore = useCallback(
    async (pageNumber: number) => {
      if (loading || !hasMore) return
      setLoading(true)
      try {
        const res = await fetchShortWaterfallList({ pageSize: 20 })
        const newData = res.data || []
        if (newData.length === 0) {
          setHasMore(false)
        } else {
          setVideoList((prev) => [...prev, ...newData])
          setPage(pageNumber + 1)
        }
      } catch (e) {
        console.error('fetchMore error', e)
      } finally {
        setLoading(false)
      }
    },
    [loading, hasMore]
  )

  const initialLoaded = useRef(false)
  useEffect(() => {
    if (initialLoaded.current) return
    initialLoaded.current = true
    fetchMore(1)
  }, [fetchMore])

  const updateIndex = (index: number) => {
    setCurrentIndex(index)
  }

  const { patchDirtyData, useMappedData } = useSharedCache()
  const mappedVideoList = useMappedData('short', videoList) as SchemaShortVideoShow[]

  const topicList = useSelector(selectTopicList)
  const reportedShortIdsRef = useRef<Set<string>>(new Set())

  const adInfo = useSelector(selectAdInfo)
  const adEntrance = useMemo(() => {
    const waterfallFull = adInfo[AdvertisementType.WaterfallFull] as SchemaOholoMaterialShow[] | undefined
    if ((waterfallFull?.length ?? 0) > 0) {
      return waterfallFull!
    }
    return (adInfo[AdvertisementType.Entrance] as SchemaOholoMaterialShow[]) ?? []
  }, [adInfo])

  const [imageCache, patchImageCache] = useImmer<ImageCache>({})

  const updateImageCache = (id: string, base64Image: string | null) => {
    if (!base64Image) return
    patchImageCache((arg) => {
      if (arg[id] == null) {
        arg[id] = {
          src: base64Image,
        }
      }
    })
  }

  const mod = (a: number, b: number) => ((a % b) + b) % b

  const adInterval = useMemo(() => {
    const waterfallFull = adInfo[AdvertisementType.WaterfallFull] as SchemaOholoMaterialShow[] | undefined
    if (waterfallFull?.[0] != null) {
      // insert_interval includes ad it self 
      //   so it need to substrate one to match our logic
      // the min result value is 1 because it will be ad on every single item otherwise
      return Math.max((waterfallFull[0].insert_interval ?? 0) - 1, 1)
    }
    return AD_INTERVAL
  }, [adInfo])

  const mapIndex = (originalIndex: number, adCount: number) => {
    if (adCount === 0) {
      return { type: 'short' as const, index: originalIndex }
    }
    const segmentLength = adInterval + 1
    let isAd = false
    let adIndex = 0
    let shortIndex = 0
    if (originalIndex >= 0) {
      isAd = (originalIndex + 1) % segmentLength === 0
      shortIndex = originalIndex - ~~(originalIndex / segmentLength)
      adIndex = ~~(originalIndex / segmentLength)
    } else {
      isAd = -(originalIndex + 1) % segmentLength === 0
      shortIndex = originalIndex - Math.floor(originalIndex / segmentLength)
      adIndex = Math.floor(originalIndex / segmentLength)
    }
    if (isAd) {
      return { type: 'ad' as const, index: adIndex }
    } else {
      return { type: 'short' as const, index: shortIndex }
    }
  }

  const selectPage = (originalIndex: number, shorts: SchemaShortVideoShow[], ads: SchemaOholoMaterialShow[]) => {
    if (shorts.length === 0) return { type: 'short' as const, item: {} as SchemaShortVideoShow, outOfRange: false }
    const index = mapIndex(originalIndex, ads.length)
    if (index.type === 'short') {
      return {
        type: 'short' as const,
        item: shorts[mod(index.index, shorts.length)],
        outOfRange: index.index !== mod(index.index, shorts.length),
      }
    } else {
      return { type: 'ad' as const, item: ads[mod(index.index, ads.length)], outOfRange: index.index < 0 }
    }
  }

  const lastTransitionIndexRef = useRef<number | null>(null)
  const [slotOrder, setSlotOrder] = useState([0, 1, 2])

  const displayIndex = lastTransitionIndexRef.current ?? currentIndex
  const prevPage = selectPage(displayIndex - 1, mappedVideoList, adEntrance)
  const currentPage = selectPage(displayIndex, mappedVideoList, adEntrance)
  const nextPage = selectPage(displayIndex + 1, mappedVideoList, adEntrance)
  // 廣告格不上報；id 也是 effect 的穩定依賴（currentPage 每次 render 都是新物件，直接依賴會不斷重置計時）
  const currentShortId = currentPage.type === 'short' ? currentPage.item?.id : undefined

  // usePlayerControl 內部用 useContext，是 React hook，只能在 render 頂層呼叫；
  // 下方分享相關 effect 只使用當次 render 拿到的 pause / play / isPaused。
  const {
    pause,
    play,
    isPaused,
    currentTime,
    remove,
    setAutoplay: setAutoplayCurrent,
  } = usePlayerControl(currentPage.item.id)
  const { play: playNext } = usePlayerControl(nextPage.item.id)
  const { play: playPrev } = usePlayerControl(prevPage.item.id)

  const alerts = useAlertWorksActionResult()

  const { wrap: wrapAddLike } = useMutex()
  const handleAddLikeList = wrapAddLike(async (video: SchemaShortVideoShow) => {
    if (video.like === TRUE) {
      // WARN: force cast string to number because js do not support int64.
      await fetchRemoveLikeList({ video_id: video?.id as unknown as number })
    } else {
      await fetchAddLikeList({ video_id: video?.id })
    }

    const nextLike = video.like === TRUE ? FALSE : TRUE
    patchDirtyData('short', video, (oldShortData) => ({
      like: nextLike,
      like_count: Math.max(
        (oldShortData.like_count || 0) + (oldShortData.like === nextLike ? 0 : nextLike === TRUE ? 1 : -1),
        0
      ),
    }))
    dispatch({ type: RESET_SHORT_FAVOURITE })
    dispatch({ type: RESET_SHORT_COLLECTION })
  })

  const { wrap: wrapAddCollection } = useMutex()
  const handleAddCollectList = wrapAddCollection(async (video: SchemaShortVideoShow) => {
    if (video.favorite === TRUE) {
      // WARN: force cast string to number because js do not support int64.
      await fetchRemoveCollectList({ video_id: video?.id as unknown as number })
    } else {
      await fetchAddCollectList({ video_id: video?.id })
    }

    const nextFavorite = video.favorite === TRUE ? FALSE : TRUE
    patchDirtyData('short', video, (oldShortData) => ({
      favorite: nextFavorite,
      favorite_count: Math.max(
        (oldShortData.favorite_count || 0) +
          (oldShortData.favorite === nextFavorite ? 0 : nextFavorite === TRUE ? 1 : -1),
        0
      ),
    }))
    dispatch({ type: RESET_SHORT_FAVOURITE })
    dispatch({ type: RESET_SHORT_COLLECTION })

    if (video.favorite === FALSE) {
      alerts.alertSaved()
    }
  })

  const preloadedImages = [
    ...(prevPage?.item?.id && imageCache[prevPage?.item?.id]?.src
      ? [{ key: (prevPage.item as SchemaShortVideoShow).coverphoto_v, value: imageCache[prevPage.item.id].src }]
      : []),
    ...(nextPage?.item?.id && imageCache[nextPage?.item?.id]?.src
      ? [{ key: (nextPage.item as SchemaShortVideoShow).coverphoto_v, value: imageCache[nextPage.item.id].src }]
      : []),
  ]

  type WaterfallFloatItem =
    | {
        type: 'icon-ui'
        id: number | string
        image: string
        item: SchemaCmsIconuiShow
      }
    | {
        type: 'ad'
        id: number | string
        image: string
        item: SchemaOholoMaterialShow
      }
  const floatAds = useMemo<WaterfallFloatItem[]>((): WaterfallFloatItem[] => {
    const waterfallFloat = adInfo[AdvertisementType.WaterfallFloat] as SchemaOholoMaterialShow[] | undefined
    if ((waterfallFloat?.length ?? 0) > 0) {
      return waterfallFloat!.map((i) => ({
        type: 'ad',
        id: i.id,
        image: i.icon_path,
        item: i,
      }))
    }
    return []
  }, [adInfo])

  const [selectedFloatAd, setSelectedFloatAd] = useState(floatAds[0])
  const [showingFloatAd, setShowingFloatAd] = useState(true)
  const openShortcutBox = useShortcutBoxHandler()

  const selectNextFloatAd = useCallback(() => {
    const currentIndex = floatAds.findIndex((i) => i.id === selectedFloatAd?.id)
    if (currentIndex === -1) {
      setSelectedFloatAd(floatAds[0])
    } else {
      setSelectedFloatAd(floatAds[(currentIndex + 1) % floatAds.length])
    }
  }, [floatAds, selectedFloatAd])

  const openFloatAd = useCallback(
    (ad: WaterfallFloatItem) => {
      if (ad.type === 'ad') {
        fetchPostAdEvent({
          date: formatDate(new Date()),
          material_id: ad.item.id,
          material_name: ad.item.name,
          oholo_site_id: ad.item.site_type,
          oholoer_id: ad.item.oholoer_id,
          oholoer_link: ad.item.oholoer_link,
          oholoer_name: ad.item.oholoer_name,
        })
        window.open(ad.item.link, '_blank')
      } else {
        openShortcutBox(ad.item)
      }
    },
    [openShortcutBox]
  )

  const closeAndRotateAd = useCallback(() => {
    setShowingFloatAd(false)
    selectNextFloatAd()
  }, [selectNextFloatAd])

  useEffect(() => {
    if (selectedFloatAd == null && floatAds.length !== 0) {
      setSelectedFloatAd(floatAds[0])
    }
  }, [floatAds, selectedFloatAd])

  const virtualPages = useRef<{ resetPageOffset: () => void } | null>(null)

  const onNext = () => {
    if (mappedVideoList.length === 0) return
    flushSync(() => {
      setAutoplayCurrent(false)
      remove(prevPage.item.id)
      pause()
      currentTime(0)
      playNext()
      const newIndex = currentIndex + 1
      // floatAd start
      const selectedItem = selectPage(newIndex, mappedVideoList, adEntrance)
      if (selectedItem.type === 'ad') {
        closeAndRotateAd()
      } else if (selectedItem.type === 'short') {
        setShowingFloatAd(true)
      }
      // floatAd end
      lastTransitionIndexRef.current = newIndex
      setSlotOrder((s) => [s[1], s[2], s[0]])
      updateIndex(newIndex)
      virtualPages.current?.resetPageOffset()

      // Load more if near the end
      if (newIndex >= mappedVideoList.length - 5) {
        fetchMore(page)
      }
    })
  }

  const onPrev = () => {
    if (mappedVideoList.length === 0) return
    flushSync(() => {
      setAutoplayCurrent(false)
      remove(nextPage.item.id)
      pause()
      currentTime(0)
      playPrev()
      const newIndex = currentIndex - 1
      // floatAd start
      const selectedItem = selectPage(newIndex, mappedVideoList, adEntrance)
      if (selectedItem.type === 'ad') {
        closeAndRotateAd()
      } else if (selectedItem.type === 'short') {
        setShowingFloatAd(true)
      }
      // floatAd end
      lastTransitionIndexRef.current = newIndex
      setSlotOrder((s) => [s[2], s[0], s[1]])
      updateIndex(newIndex)
      virtualPages.current?.resetPageOffset()
    })
  }

  const onScrollPast50 = (targetPage: 'prev' | 'next') => {
    pause()
    if (targetPage === 'prev') {
      playPrev()
    } else {
      playNext()
    }
  }

  const pageActivated = usePageActivated()

  // N1-5268：瀑布流上報（看滿 WATCH_REPORT_DELAY 才算一次；同一頁面實例每支只報一次）
  const reportWatchedShort = useEvent((id: string) => {
    if (reportedShortIdsRef.current.has(id)) return
    const item = mappedVideoList.find((v) => v?.id === id)
    if (!item?.title) return
    reportedShortIdsRef.current.add(id)
    reportShortVideoClick(item, topicList)
  })

  useEffect(() => {
    // 頁面非活躍（切到其他 tab）不計時；切回來重新起算，避免把背景停留算成觀看
    if (!pageActivated || currentShortId == null) return
    if (reportedShortIdsRef.current.has(currentShortId)) return
    const timer = setTimeout(() => reportWatchedShort(currentShortId), WATCH_REPORT_DELAY)
    return () => clearTimeout(timer)
  }, [pageActivated, currentShortId, reportWatchedShort])

  // ---- 影片分享（鏡像 ViewVideo 的分享流程；contract 見 features/share-shortvideo-waterfall） ----
  const enableShare = useSelector(selectEnableShare)
  const { shareUrl, fallbackToShare } = useSelector(selectShareVideoData)
  const { openHintModal } = useHintModal()

  /** 觸發當下的快照；同時是「這輪分享結果是不是本頁發起的」的 owner 標記 */
  const shareSourceRef = useRef<{
    id: string
    title?: string
    coverphoto?: string
    posterImg?: string
  } | null>(null)
  /** 開 modal 前的播放狀態，用於關閉後恢復 */
  const playbackRef = useRef<{ pausedItemId: string; wasPlaying: boolean } | null>(null)
  /** modal 是否真的開過；shareUrl 初始即為 null，沒有這個標記會在請求途中誤清 owner */
  const modalOpenedRef = useRef(false)

  const handleShare = useEvent((item: SchemaShortVideoShow) => {
    if (!enableShare) {
      pause()
      openShareModal()
      shareSourceRef.current = null
      return
    }
    shareSourceRef.current = {
      id: item.id,
      title: item.title,
      // 卡片封面容器是 16:9，必須用橫向封面；posterImg 保持 undefined，
      // 否則 Modal 會改用瀑布流快取的直向圖而被裁切。
      // 短視頻只有直向封面：全 repo 的短視頻路徑（列表縮圖、播放器、快取）都取 coverphoto_v，
      // coverphoto_h 沒有任何既有使用者，不能假設後端有填
      coverphoto: item.coverphoto_v,
      posterImg: undefined,
    }
    dispatch(shareVideoActions.fetchShareVideo(item.id))
    // 這裡不暫停影片：fail-closed 路徑不會有 shareUrl 轉 falsy，暫停了就再也不會恢復。
  })

  // 成功：開分享 modal，並暫停「當下真正在播的那支」（慢網路下可能已不是發起分享的那支）
  useEffect(() => {
    if (!shareUrl) return
    if (!pageActivated) return
    if (!shareSourceRef.current) return
    // 只消費一次：modal 開啟期間若因 pageActivated / currentShortId 變動讓 effect 重跑，
    // 會重開 modal 並用「已暫停」的當下狀態覆寫 playbackRef，導致關閉後不恢復播放
    if (modalOpenedRef.current) return
    if (currentShortId) {
      // 順序不可顛倒：先讀 isPaused 再 pause，反過來 wasPlaying 會恆為 false
      playbackRef.current = { pausedItemId: currentShortId, wasPlaying: !isPaused() }
      pause()
    }
    openHintModal({ children: ShareVideoModal, props: shareSourceRef.current })
    modalOpenedRef.current = true
  }, [shareUrl, pageActivated, currentShortId, isPaused, pause, openHintModal])

  // 離開本頁時放棄仍在途中的分享。
  //
  // 不這麼做會有兩種壞結果：
  // (1) 結果延後到返回時才被消費 —— 使用者沒按任何鍵就被彈出分享卡，
  //     降級路徑更糟，會被動跳轉到推廣頁並失去瀏覽位置；
  // (2) 若期間在別頁發起分享，saga 的 takeLatest 會取消本次請求，
  //     成功與降級兩個 effect 都不會觸發，shareSourceRef 將無限期卡在已設值狀態，
  //     直到下次全域 shareUrl 變動時被誤用（拿舊快照配別人的連結開 modal）。
  //
  // 只清自己的 ref，不碰全域 state —— 此時別頁可能已發起自己的分享，
  // 在背景 dispatch clearShareData 會把對方的結果殺掉。
  useEffect(() => {
    if (pageActivated) return
    if (!shareSourceRef.current) return
    if (modalOpenedRef.current) return // modal 已開啟：交由既有的關閉流程收尾
    shareSourceRef.current = null
    playbackRef.current = null
  }, [pageActivated])

  // 降級：開全屏分享彈窗（內容同通用推廣頁）。彈窗關閉後不自動恢復播放。
  useEffect(() => {
    if (!fallbackToShare) return
    if (!pageActivated) return
    if (!shareSourceRef.current) return
    pause()
    openShareModal()
    dispatch(shareVideoActions.clearShareData())
    shareSourceRef.current = null
  }, [fallbackToShare, pageActivated, pause, openShareModal, dispatch])

  // modal 關閉（shareUrl 轉 falsy）：恢復播放並清狀態。
  // 必須 edge-triggered——shareUrl 初始就是 null，deps 變動會讓此 effect 在請求途中重跑。
  useEffect(() => {
    if (shareUrl) return
    if (!modalOpenedRef.current) return
    const playback = playbackRef.current
    const shouldResume = Boolean(playback?.wasPlaying) && currentShortId === playback?.pausedItemId
    if (shouldResume) {
      // 頁面非活躍時只是「還不能播」，不是「不用播」——
      // 此時若連同旗標一起清掉，之後重新活躍也不會再恢復，影片永久停在暫停。
      // deps 含 pageActivated，保留旗標直接 return 即可讓它在重新活躍時補播。
      if (!pageActivated) return
      play()
    }
    shareSourceRef.current = null
    playbackRef.current = null
    modalOpenedRef.current = false
  }, [shareUrl, pageActivated, currentShortId, play])

  if (mappedVideoList.length === 0) {
    return <Wrapper />
  }

  return (
    <PageWrapperNonScroll>
      <Wrapper>
        <VirtualPages ref={virtualPages} onNext={onNext} onPrev={onPrev} onScrollPast50={onScrollPast50} hasFooter>
          {!prevPage.outOfRange ? (
            <PageWrapperInner
              key={`instance-${slotOrder[0]}`}
              pageActivated={pageActivated}
              imageCache={imageCache}
              updateImageCache={updateImageCache}
              preloadedImages={preloadedImages}
              autoplay={false}
              handleAddLikeList={handleAddLikeList}
              handleAddCollectList={handleAddCollectList}
              onShare={handleShare}
              videoInfo={prevPage.item}
              type={prevPage.type}
              toolbarStyle="home"
            />
          ) : undefined}
          <PageWrapperInner
            key={`instance-${slotOrder[1]}`}
            pageActivated={pageActivated}
            imageCache={imageCache}
            updateImageCache={updateImageCache}
            preloadedImages={preloadedImages}
            autoplay={true}
            handleAddLikeList={handleAddLikeList}
            handleAddCollectList={handleAddCollectList}
            onShare={handleShare}
            videoInfo={currentPage.item}
            type={currentPage.type}
            toolbarStyle="home"
          />
          {!nextPage.outOfRange ? (
            <PageWrapperInner
              key={`instance-${slotOrder[2]}`}
              pageActivated={pageActivated}
              imageCache={imageCache}
              updateImageCache={updateImageCache}
              preloadedImages={preloadedImages}
              autoplay={false}
              handleAddLikeList={handleAddLikeList}
              handleAddCollectList={handleAddCollectList}
              onShare={handleShare}
              videoInfo={nextPage.item}
              type={nextPage.type}
              toolbarStyle="home"
            />
          ) : undefined}
        </VirtualPages>
        <SearchButton onClick={() => navigateToSearchViaSpecific({ in: 'short' })}>
          <IconSearch />
        </SearchButton>
        {showingFloatAd && selectedFloatAd != null && (
          <FloatAdWrapper>
            <FloatAdImage src={selectedFloatAd.image} onClick={() => openFloatAd(selectedFloatAd)} />
            <FloatAdCloseButton onClick={closeAndRotateAd}>
              <FloatAdCloseIcon />
            </FloatAdCloseButton>
          </FloatAdWrapper>
        )}
      </Wrapper>
    </PageWrapperNonScroll>
  )
}

export default function WaterfallWrapper() {
  return (
    <PlayerControlProvider>
      <Waterfall />
    </PlayerControlProvider>
  )
}
